import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import type { RoleType, UsersMetadataRow } from '@/lib/database.types';

export interface UserListRow extends UsersMetadataRow {
  email: string | null;
}

export function useUsersList() {
  return useQuery({
    queryKey: ['users_list'],
    queryFn: async (): Promise<UserListRow[]> => {
      const { data, error } = await supabase.from('users_metadata').select('*').order('full_name');
      if (error) throw error;
      // Email lookup via RPC nécessite une view exposée. Pour le sprint 1 on le laisse null
      // (sera enrichi via un endpoint admin Sprint 2).
      return data.map((u) => ({ ...u, email: null }));
    },
  });
}

export interface UpdateUserMetaInput {
  user_id: string;
  full_name?: string | null;
  role?: RoleType;
  entreprise_id?: string | null;
  activites_ids?: string[];
  permissions?: Record<string, boolean> | null;
}

export function useUpdateUserMeta() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: UpdateUserMetaInput): Promise<UsersMetadataRow> => {
      const { user_id, ...rest } = input;
      const { data, error } = await supabase
        .from('users_metadata')
        .update(rest)
        .eq('user_id', user_id)
        .select('*')
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['users_list'] });
    },
  });
}
